// The talon knife: "Counter strike talon knife" by Mat_Dhyne (sketchfab.com/Mat_Dhyne, CC-BY-4.0),
// cut by scripts/import-talon.mjs into the steel ring, tang and blade, the handle scales and the pins.
// All the steel wears the finish (like CS2, the ring too); the scales are ivory, the pins follow the
// finish's accent colour.
//
// Held like the karambit (karambitModel.ts): index finger through the ring, the claw past the pinky,
// and the same open-palm inspect. So it's placed in the procedural karambit's coordinates: handle
// along +X from the ring toward the blade, the hook toward -Y; the arm finds the parts named "ring"
// and "blade" for the palm inspect (fpArms.ts measureKnife).

import * as THREE from 'three';
import type { KnifeModel } from './karambitModel';
import { projectBladeUvs } from './bladeUvs';
import type { KnifeGrip } from './fpArms';

const TALON_URL = `${import.meta.env.BASE_URL ?? '/'}assets/models/talon.glb`;

/** Model units -> procedural knife units (the arm scales the knife to the fist; proportions matter). */
const FIT_SCALE = 0.08;
/** Where the blade starts along the model's Y (scripts/import-talon.mjs BLADE_FROM). */
const BLADE_FROM = 17.5;
/** How much of the ring-to-blade length the fist covers. */
const FIST_SHARE = 0.7;
/** The tilt up through the fist (radians), round this point of the grip (knife units from the ring). */
const TILT = 0.6;
const GRIP_PIVOT = 0.55;
/** Lean forward in the fist (radians), round this axis of the turned knife (measured in game). */
const LEAN = 0.45;
const LEAN_AXIS = new THREE.Vector3(-0.938, 0.346, 0).normalize();

let loading: Promise<KnifeModel | null> | null = null;

/** Load once. `blade` is the finish material (shared with the other knives). */
export function loadTalon(blade: THREE.Material): Promise<KnifeModel | null> {
  loading ??= (async () => {
    try {
      const [{ GLTFLoader }, { toCreasedNormals, mergeGeometries }] = await Promise.all([
        import('three/addons/loaders/GLTFLoader.js'),
        import('three/addons/utils/BufferGeometryUtils.js'),
      ]);
      const gltf = await new GLTFLoader().loadAsync(TALON_URL);
      gltf.scene.updateMatrixWorld(true);
      const parts = new Map<string, THREE.BufferGeometry>();
      gltf.scene.traverse((o) => {
        if (o instanceof THREE.Mesh)
          parts.set(o.name.replace(/_\d+$/, ''), o.geometry.clone().applyMatrix4(o.matrixWorld));
      });
      const ring = parts.get('ring');
      const tang = parts.get('tang');
      const bladeGeo = parts.get('blade');
      const scales = parts.get('scales');
      const pins = parts.get('pins');
      if (!ring || !tang || !bladeGeo || !scales || !pins) return null;

      // Model frame: the knife runs along +Y from the ring to the tip, the claw hooks toward -X (knife
      // +Y here, which the arm curls up past the knuckles like the karambit), the flat sides face Z.
      const ringC = new THREE.Box3()
        .setFromBufferAttribute(ring.getAttribute('position') as THREE.BufferAttribute)
        .getCenter(new THREE.Vector3());
      const ax = new THREE.Vector3(0, 1, 0);
      const ay = new THREE.Vector3(-1, 0, 0);
      const az = new THREE.Vector3().crossVectors(ax, ay);

      // One finish across all the steel: ring, tang and blade share the projection.
      const steel = mergeGeometries([
        ring.deleteAttribute('uv'),
        tang.deleteAttribute('uv'),
        bladeGeo.clone().deleteAttribute('uv'),
      ]);
      if (!steel) return null;
      projectBladeUvs(steel, ringC, ax, ay);
      const ivory = new THREE.MeshPhongMaterial({ color: 0xd9d3c4 });
      const accent = new THREE.MeshPhongMaterial({ color: 0x1b2f7a });

      const inner = new THREE.Group();
      const add = (name: string, geo: THREE.BufferGeometry, mat: THREE.Material, visible = true) => {
        const mesh = new THREE.Mesh(toCreasedNormals(geo, (35 * Math.PI) / 180), mat);
        mesh.name = name;
        mesh.frustumCulled = false;
        mesh.visible = visible;
        inner.add(mesh);
      };
      add('steel', steel, blade);
      add('scales', scales, ivory);
      add('pins', pins, accent);
      // Where the arm looks for the ring and the blade (hidden: the steel above draws them).
      add('ring', ring, ivory, false);
      add('blade', bladeGeo, ivory, false);

      const basis = new THREE.Matrix4().makeBasis(ax, ay, az).transpose();
      // How CS2 holds it in the fist: the claw turned over and the knife tilted up (round the
      // middle of the grip), so the ring sits low by the index finger and the claw rises at the right.
      const pivot = new THREE.Vector3(GRIP_PIVOT, 0, 0);
      const turn = new THREE.Matrix4()
        .makeRotationX(Math.PI)
        .multiply(new THREE.Matrix4().makeRotationZ(TILT));
      // Then leaned forward round the same point, so the point aims out and away from the body.
      const lean = new THREE.Matrix4().makeRotationAxis(LEAN_AXIS, LEAN);
      const aboutPivot = (m: THREE.Matrix4) =>
        new THREE.Matrix4().makeTranslation(pivot.clone().sub(pivot.clone().applyMatrix4(m))).multiply(m);
      const held = aboutPivot(turn).multiply(aboutPivot(lean));
      inner.applyMatrix4(
        new THREE.Matrix4()
          .makeScale(FIT_SCALE, FIT_SCALE, FIT_SCALE)
          .multiply(basis)
          .multiply(new THREE.Matrix4().makeTranslation(-ringC.x, -ringC.y, -ringC.z)),
      );
      const object = new THREE.Group();
      object.add(inner);
      // The talon's handle is long for its claw: the fist covers only part of the ring-to-blade
      // length (fpArms.ts KnifeGrip), so the knife sits bigger and the claw clears the hand.
      const grip: KnifeGrip = {
        index: new THREE.Vector3(0, 0, 0),
        handle: (BLADE_FROM - ringC.y) * FIT_SCALE * FIST_SHARE,
        // In the fist only: the palm inspect holds it as it comes, the claw hanging down.
        restTurn: held,
      };
      object.userData.grip = grip;
      return { object, setAccent: (c: string) => accent.color.set(c) };
    } catch {
      return null;
    }
  })();
  return loading;
}
